import React, { createContext, useContext, useEffect, useState } from 'react';
import Script from 'next/script';
import { useAppConfigContext } from '@/context/AppContext';
import dlv from 'dlv';
import { GenericError } from '@/errors/GenericServerError';
import { otpVerify } from '@/actions/ecom/otpVerify';
import { getEnvValue } from '@/utils/envUtils';

interface KwikPassContextType {
  isLoaded: boolean;
  kpSendOTP: (phone: { mobile_number: string }) => Promise<any>;
  kpVerifyOTP: (data: { mobile_number: string; otp: string | number }) => Promise<any>;
  handleKPLogout: () => void;
  kwikPassLogin: boolean;
}

const KwikPassContext = createContext<KwikPassContextType | null>(null);

export const useKwikPass = () => {
  const context = useContext(KwikPassContext);
  if (!context) {
    // Return a default object during SSR or when not wrapped in provider
    if (typeof window === 'undefined') {
      return {
        isLoaded: false,
        kpSendOTP: async () => {
          throw new Error('KwikPass not available during SSR');
        },
        kpVerifyOTP: async () => {
          throw new Error('KwikPass not available during SSR');
        },
        handleKPLogout: () => {},
        kwikPassLogin: false,
      };
    }
    throw new Error('useKwikPass must be used within KwikPassProvider');
  }
  return context;
};

interface KwikPassProviderProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    merchantInfo?: {
      environment: string;
      mid: string;
      type: string;
      integrationType: string;
    };
    __KP_LOGIN_SDK_INSTANCE__?: any;
    kpqueue?: any[];
  }
}

export const KwikPassProvider: React.FC<KwikPassProviderProps> = ({ children }) => {
  const environment = getEnvValue('KWIKPASS_ENV');
  const merchantId = getEnvValue('KWIKPASS_MERCHANT_ID');
  const [isLoaded, setIsLoaded] = useState(false);

  const { state: appConfig } = useAppConfigContext();
  const { kwikPassLogin } = dlv(appConfig, 'config.goKwik', {
    kwikPassLogin: false,
  });

  useEffect(() => {
    // Set merchant info
    window.merchantInfo = {
      ...window.merchantInfo,
      environment,
      mid: merchantId,
      type: 'merchantInfo',
      integrationType: 'CUSTOM_HEADLESS',
    };

    // Initialize KwikPass SDK instance
    window.__KP_LOGIN_SDK_INSTANCE__ = window.__KP_LOGIN_SDK_INSTANCE__ || {};
    window.__KP_LOGIN_SDK_INSTANCE__.logEvents =
      window.__KP_LOGIN_SDK_INSTANCE__.logEvents ||
      function (event: any) {
        window.kpqueue = window.kpqueue || [];
        window.kpqueue.push(event);
      };
  }, [merchantId, environment]);

  // Provide default context even when disabled
  if (!kwikPassLogin || !merchantId || !environment) {
    return (
      <KwikPassContext.Provider
        value={{
          kwikPassLogin: false,
          isLoaded: false,
          kpSendOTP: async () => {
            throw new Error('KwikPass is disabled');
          },
          kpVerifyOTP: async () => {
            throw new Error('KwikPass is disabled');
          },
          handleKPLogout: () => {},
        }}
      >
        {children}
      </KwikPassContext.Provider>
    );
  }

  const handleScriptLoad = () => {
    setIsLoaded(true);
  };

  const kpSendOTP = async (payload: { mobile_number: string }) => {
    if (window.__KP_LOGIN_SDK_INSTANCE__?.kpSendOTP && payload?.mobile_number) {
      return await window.__KP_LOGIN_SDK_INSTANCE__.kpSendOTP(payload?.mobile_number);
    }
    throw new Error('Either KwikPass SDK not loaded or mobile number is missing');
  };

  const kpVerifyOTP = async (data: { mobile_number: string; otp: string | number }) => {
    try {
      if (window.__KP_LOGIN_SDK_INSTANCE__?.kpVerifyOTP && data?.mobile_number && data?.otp) {
        const resp = await window.__KP_LOGIN_SDK_INSTANCE__.kpVerifyOTP({
          phone: data.mobile_number,
          otp: Number(data.otp),
        });

        if (resp.status !== 200) {
          throw new GenericError(resp?.message, resp.status, resp.message, resp);
        }

        const verifyData = await otpVerify({
          kpToken: resp?.body?.data?.kpToken,
          mobile_number: data.mobile_number,
          otp: String(data.otp),
        });

        return verifyData;
      } else {
        let message = 'Either KwikPass SDK not loaded or required data is missing';
        throw new GenericError(message, 500, message, window.__KP_LOGIN_SDK_INSTANCE__);
      }
    } catch (error: any) {
      const { statusCode, message } = error || {};
      throw new GenericError(message, statusCode, 'message', {
        error: message,
      });
    }
  };

  const handleKPLogout = () => {
    if (window.__KP_LOGIN_SDK_INSTANCE__?.handleKPLogout) {
      window.__KP_LOGIN_SDK_INSTANCE__.handleKPLogout();
    }
  };

  const scriptSrc =
    environment === 'production'
      ? 'https://pdp.gokwik.co/kwikpass/plugin/build/kp-custom-merchant.js'
      : 'https://sandbox.pdp.gokwik.co/kwikpass/plugin/build/kp-custom-merchant.js';

  return (
    <KwikPassContext.Provider
      value={{
        kwikPassLogin: kwikPassLogin && isLoaded,
        isLoaded,
        kpSendOTP,
        kpVerifyOTP,
        handleKPLogout,
      }}
    >
      <Script
        src={scriptSrc}
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
        onError={(e) => console.error('Failed to load KwikPass SDK:', e)}
      />
      {children}
    </KwikPassContext.Provider>
  );
};
